/* theiro cart: package-protection toggle, next-ball upsell, discount codes, game reward.
   All theiro cart writes are serialised and re-render Dawn's drawer through the Section Rendering API. */
(() => {
  const doc = document;
  const root = doc.documentElement;
  const cfg = window.theiro || {};
  const FALLBACK = { emit: () => {}, safeStore: () => null, announce: () => {} };
  /* Resolve helpers at call time: theiro.js may execute after this file. */
  const util = new Proxy({}, { get: (_, key) => (window.theiroUtil || FALLBACK)[key] });
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const REWARD_KEY = 'theiro_reward';
  const REWARD_DONE_KEY = 'theiro_reward_done';
  const MSG_KEY = 'tg_cart_msg';
  const GENERIC_ERROR = 'Something went wrong. Please try again.';
  let queue = Promise.resolve();
  let pendingMessage = null;

  const js = (route) => (route.endsWith('.js') ? route : `${route}.js`);
  const drawer = () => doc.querySelector('cart-drawer');
  const onCartPage = () => Boolean(doc.querySelector('cart-items'));
  /* On /cart the drawer copy comes first in the DOM; prefer the cart-page copy. */
  const state = () => {
    const el = doc.querySelector('#main-cart-footer [data-tg-cart-state]') || doc.querySelector('[data-tg-cart-state]');
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  };
  const ppId = () => Number(state()?.ppVariantId || cfg.protectionVariantId || 0);
  const fetchCart = async () => {
    const res = await fetch(js(window.routes.cart_url), { headers: { Accept: 'application/json' } });
    if (!res.ok) throw Object.assign(new Error(`Cart request failed (${res.status})`), { status: res.status });
    return res.json();
  };
  const post = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || `Cart request failed (${res.status})`);
      err.status = res.status || 500;
      throw err;
    }
    return data;
  };

  function showMessage(text, isError = false) {
    util.announce(text);
    const els = [...doc.querySelectorAll('[data-tg-code-msg]')];
    const el = els.find((m) => m.closest('#main-cart-footer')) || els[0];
    if (!el) return;
    el.closest('details')?.setAttribute('open', '');
    el.textContent = text;
    el.classList.toggle('is-error', isError);
  }

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  /* Swap drawer + bubble markup without opening the drawer (Dawn's renderContents always opens it). */
  const swapSections = (data) => {
    const d = drawer();
    const src = parse(data.sections['cart-drawer']).querySelector('#CartDrawer');
    const dst = doc.getElementById('CartDrawer');
    if (src && dst) dst.innerHTML = src.innerHTML;
    const bubbleHtml = data.sections['cart-icon-bubble'];
    const bubbleSrc = bubbleHtml && parse(bubbleHtml).querySelector('.shopify-section');
    const bubble = doc.getElementById('cart-icon-bubble');
    if (bubbleSrc && bubble) bubble.innerHTML = bubbleSrc.innerHTML;
    doc.getElementById('CartDrawer-Overlay')?.addEventListener('click', () => d.close());
  };
  const focusTarget = () => {
    const a = doc.activeElement;
    if (a?.closest?.('[data-tg-protect]')) return '[data-tg-protect]';
    if (a?.closest?.('[data-tg-code-form]')) return '[data-tg-code-form] input';
    if (a?.closest?.('[data-tg-upsell]')) return '[data-tg-upsell-add]';
    return null;
  };
  const render = (data) => {
    if (onCartPage()) {
      if (pendingMessage) util.safeStore(sessionStorage, MSG_KEY, JSON.stringify(pendingMessage));
      window.location.reload();
      return;
    }
    const d = drawer();
    if (!d || !data || !data.sections || !data.sections['cart-drawer']) return;
    const focusSel = focusTarget();
    const open = d.classList.contains('active');
    const keepTop = d.querySelector('cart-drawer-items')?.scrollTop || 0;
    if (open) d.renderContents(data); else swapSections(data);
    const list = d.querySelector('cart-drawer-items');
    if (list) list.scrollTop = keepTop;
    if (typeof data.item_count === 'number') {
      const empty = data.item_count === 0;
      d.classList.toggle('is-empty', empty);
      d.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
    }
    if (open) {
      const next = (focusSel && d.querySelector(focusSel))
        || (d.classList.contains('is-empty') ? d.querySelector('.drawer__inner-empty a') : null);
      next?.focus({ preventScroll: true });
    }
    afterRender();
  };
  const run = (task) => {
    queue = queue.then(task).catch((err) => {
      const text = err.status === 429
        ? 'Too many requests — try again in a moment.'
        : (err.status ? err.message : GENERIC_ERROR);
      showMessage(text, true);
    });
    return queue;
  };

  /* Never leave protection without a ball (or when the feature is off), keep it at quantity 1.
     Reads the cart state when the task runs, not when it is queued. */
  const enforceProtection = () => run(async () => {
    const s = state();
    if (!s || !s.ppVariantId || !s.ppQty) return;
    const orphan = s.otherQty === 0 || s.ballQty === 0 || s.ppEnabled === false;
    const quantity = orphan ? 0 : (s.ppQty > 1 ? 1 : null);
    if (quantity === null) return;
    render(await post(js(window.routes.cart_change_url), { id: String(s.ppVariantId), quantity }));
  });

  function afterRender() {
    if (pendingMessage) { showMessage(pendingMessage.text, pendingMessage.error); pendingMessage = null; }
    /* A class on <html> survives Dawn's own drawer re-fetch after add-to-cart. */
    if (window.theiroJustAdded && Date.now() - window.theiroJustAdded < 5000) {
      window.theiroJustAdded = 0;
      root.classList.add('tg-just-added');
      setTimeout(() => root.classList.remove('tg-just-added'), 5000);
    }
    if (util.safeStore(sessionStorage, 'tg_reduced')) root.classList.add('tg-no-upsell');
    util.initDelivery?.(doc.querySelector('cart-drawer') || doc);
    enforceProtection();
  }

  /* ---------- protection toggle ---------- */
  doc.addEventListener('click', (e) => {
    if (e.target.closest('a')) return;
    const card = e.target.closest('[data-tg-protect-card]');
    const toggle = e.target.closest('[data-tg-protect]') || card?.querySelector('[data-tg-protect]');
    if (!toggle || toggle.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    toggle.focus({ preventScroll: true }); /* taps don't focus buttons on iOS; render() restores focus to it */
    const variantId = Number(toggle.dataset.variantId || ppId());
    if (!variantId) return;
    const on = toggle.getAttribute('aria-checked') === 'true';
    toggle.setAttribute('aria-busy', 'true');
    toggle.setAttribute('aria-checked', on ? 'false' : 'true');
    run(() => (on
      ? post(js(window.routes.cart_change_url), { id: String(variantId), quantity: 0 })
      : post(js(window.routes.cart_add_url), { items: [{ id: variantId, quantity: 1 }] })
    ).then((data) => {
      util.emit(on ? 'protection_off' : 'protection_on', {});
      if (!on) window.Mascot?.set?.('cheer');
      render(data);
    }).catch((err) => {
      toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      toggle.removeAttribute('aria-busy');
      throw err;
    }));
  });

  /* ---------- next-ball upsell ---------- */
  doc.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tg-upsell-add]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    const box = btn.closest('[data-tg-upsell]');
    const id = Number(box?.querySelector('[data-tg-upsell-variant]')?.value || btn.dataset.variantId);
    if (!id) return;
    btn.setAttribute('aria-busy', 'true');
    run(() => post(js(window.routes.cart_add_url), { items: [{ id, quantity: 1 }] }).then((data) => {
      util.emit('cart_upsell_add', { id });
      window.theiroJustAdded = Date.now();
      window.Mascot?.set?.('celebrate');
      render(data);
    }).catch((err) => { btn.removeAttribute('aria-busy'); throw err; }));
  });
  const markReduced = () => {
    util.safeStore(sessionStorage, 'tg_reduced', '1');
    root.classList.add('tg-no-upsell');
  };
  doc.addEventListener('click', (e) => {
    if (e.target.closest('[data-tg-upsell-dismiss]') || e.target.closest('cart-remove-button')) markReduced();
  });
  doc.addEventListener('change', (e) => {
    /* Keep the upsell price in step with the chosen size. */
    const sel = e.target.closest('[data-tg-upsell-variant]');
    if (sel) {
      const opt = sel.selectedOptions[0];
      const box = sel.closest('[data-tg-upsell]');
      const set = (selector, value) => { const el = box?.querySelector(selector); if (el && value) el.textContent = value; };
      set('[data-tg-upsell-marginal]', opt?.dataset.marginal);
      set('[data-tg-upsell-price]', opt?.dataset.price);
      return;
    }
    /* A shopper who lowers a quantity has decided; stop suggesting more balls this session. */
    const input = e.target.closest('cart-drawer-items .quantity__input, cart-items .quantity__input');
    if (input && Number(input.value) < Number(input.getAttribute('value'))) markReduced();
  });

  /* ---------- discount codes ---------- */
  /* Reads the live code list inside the queued task so concurrent applies never drop each other. */
  const applyCodes = (mutate, newCode) => run(async () => {
    const cart = await fetchCart();
    const kept = (cart.discount_codes || [])
      .filter((d) => d.applicable !== false)
      .map((d) => String(d.code).toUpperCase());
    const codes = [...new Set(mutate(kept))];
    const data = await post(js(window.routes.cart_update_url), { discount: codes.join(',') });
    if (newCode) {
      const hit = (data.discount_codes || []).find((d) => String(d.code).toUpperCase() === newCode);
      pendingMessage = hit && hit.applicable !== false
        ? { text: `${newCode} applied.`, error: false }
        : { text: `${newCode} can't be used with this cart.`, error: true };
    }
    render(data);
    return data;
  });
  doc.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-tg-code-form]');
    if (!form) return;
    e.preventDefault();
    const code = String(new FormData(form).get('code') || '').trim().toUpperCase();
    if (!code) return;
    applyCodes((codes) => [...codes.filter((c) => c !== code), code], code);
  });
  doc.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-tg-code-remove]');
    if (!rm) return;
    const code = String(rm.dataset.tgCodeRemove || '').toUpperCase();
    if (!code) return;
    if (code === String(util.safeStore(localStorage, REWARD_KEY) || '').toUpperCase()) {
      util.safeStore(localStorage, REWARD_DONE_KEY, code);
    }
    applyCodes((codes) => codes.filter((c) => c !== code));
  });

  /* ---------- game reward (public API used by assets/glow-game.js) ---------- */
  /** A cart that only holds package protection counts as empty. */
  const isEffectivelyEmpty = (cart) => !cart || !(cart.items || []).some((item) => item.variant_id !== ppId());
  /** Applies a code while keeping codes already in the cart. Resolves 'applied' | 'pending' | 'invalid' | 'error'. */
  const applyDiscount = async (code) => {
    const upper = String(code || '').trim().toUpperCase();
    if (!upper) return 'invalid';
    util.safeStore(localStorage, REWARD_KEY, upper);
    const cart = await fetchCart().catch(() => null);
    if (isEffectivelyEmpty(cart)) return 'pending';
    const live = (cart.discount_codes || []).some((d) => String(d.code).toUpperCase() === upper && d.applicable !== false);
    if (!live) {
      const data = await applyCodes((codes) => [...codes.filter((c) => c !== upper), upper], upper);
      if (!data) return 'error';
      const hit = (data.discount_codes || []).find((d) => String(d.code).toUpperCase() === upper);
      if (!hit || hit.applicable === false) return 'invalid';
    }
    util.safeStore(localStorage, REWARD_DONE_KEY, upper);
    return 'applied';
  };
  window.theiroCart = { applyDiscount, isEffectivelyEmpty, refresh: enforceProtection };

  doc.addEventListener('glow:atc-success', () => {
    const reward = String(util.safeStore(localStorage, REWARD_KEY) || '').toUpperCase();
    if (!reward || util.safeStore(localStorage, REWARD_DONE_KEY) === reward) return;
    setTimeout(() => applyDiscount(reward), 700);
  });

  const boot = () => {
    if (util.safeStore(sessionStorage, 'tg_reduced')) root.classList.add('tg-no-upsell');
    const saved = util.safeStore(sessionStorage, MSG_KEY);
    if (saved) {
      util.safeStore(sessionStorage, MSG_KEY, null);
      try { const m = JSON.parse(saved); showMessage(m.text, m.error); } catch (e) { /* ignore a corrupt message */ }
    }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(afterRender, 50));
    }
    enforceProtection();
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
})();
